<template>
  <v-app class="bg-white">
    <!-- Loading overlay -->
    <v-overlay v-model="loading" class="text-center">
      <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
      <div class="mt-3">Loading vehicles...</div>
    </v-overlay>

    <!-- Public Header -->
    <PublicHeader />

    <v-main :style="$vuetify.breakpoint.xs ? 'padding:0px!important;margin-top: 70px;' : 'padding:0px!important;margin-top: 70px;'">

      <!-- Search and Filter Header -->
      <section class="search-header">
        <v-container>
          <div class="search-content">
            <div class="search-title-section">
              <h1 class="search-title">Cars for Sale</h1>
              <p class="search-subtitle" v-if="totalVehicles > 0">
                {{ totalVehicles.toLocaleString() }} vehicles available
              </p>
            </div>

            <!-- Search and Filter Bar -->
            <div class="search-filter-bar">
              <div class="search-input-section">
                <v-text-field
                  v-model="searchQuery"
                  placeholder="Search by make, model, or keyword"
                  outlined
                  dense
                  hide-details
                  class="search-input"
                  @input="onSearchInput"
                >
                </v-text-field>
              </div>

              <div class="filter-buttons">
                <v-btn
                  outlined
                  class="filter-btn"
                  @click="showFilters = !showFilters"
                  :color="hasActiveFilters ? 'primary' : ''"
                >
                  <v-icon left class="material-icons-round">tune</v-icon>
                  Filters
                  <v-badge
                    v-if="activeFiltersCount > 0"
                    :content="activeFiltersCount"
                    color="primary"
                    overlap
                  ></v-badge>
                </v-btn>

                <v-menu offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      outlined
                      class="filter-btn"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon left class="material-icons-round">sort</v-icon>
                      Sort: {{ currentSort.label }}
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item
                      v-for="sort in sortOptions"
                      :key="sort.value"
                      @click="changeSortOrder(sort)"
                      :class="{ 'v-list-item--active': currentSort.value === sort.value }"
                    >
                      <v-list-item-title>{{ sort.label }}</v-list-item-title>
                      <v-list-item-action v-if="currentSort.value === sort.value">
                        <v-icon class="material-icons-round">check</v-icon>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </div>
          </div>
        </v-container>
      </section>

      <!-- Filter Panel -->
      <v-expand-transition>
        <section v-show="showFilters" class="filter-panel">
          <v-container>
            <v-card class="filter-card" flat>
              <v-card-text class="filter-content">
                <v-row>
                  <!-- Price Range -->
                  <v-col cols="12" md="3">
                    <div class="filter-group">
                      <h4 class="filter-title">Price Range</h4>
                      <v-row>
                        <v-col cols="6">
                          <v-text-field
                            v-model="filters.minPrice"
                            label="Min Price"
                            prefix="$"
                            type="number"
                            outlined
                            dense
                            hide-details
                          ></v-text-field>
                        </v-col>
                        <v-col cols="6">
                          <v-text-field
                            v-model="filters.maxPrice"
                            label="Max Price"
                            prefix="$"
                            type="number"
                            outlined
                            dense
                            hide-details
                          ></v-text-field>
                        </v-col>
                      </v-row>
                    </div>
                  </v-col>

                  <!-- Make -->
                  <v-col cols="12" md="3">
                    <div class="filter-group">
                      <h4 class="filter-title">Make</h4>
                      <v-select
                        v-model="filters.make"
                        :items="makeOptions"
                        label="Select Make"
                        outlined
                        dense
                        hide-details
                        clearable
                      ></v-select>
                    </div>
                  </v-col>

                  <!-- Year Range -->
                  <v-col cols="12" md="3">
                    <div class="filter-group">
                      <h4 class="filter-title">Year</h4>
                      <v-row>
                        <v-col cols="6">
                          <v-text-field
                            v-model="filters.minYear"
                            label="Min Year"
                            type="number"
                            outlined
                            dense
                            hide-details
                          ></v-text-field>
                        </v-col>
                        <v-col cols="6">
                          <v-text-field
                            v-model="filters.maxYear"
                            label="Max Year"
                            type="number"
                            outlined
                            dense
                            hide-details
                          ></v-text-field>
                        </v-col>
                      </v-row>
                    </div>
                  </v-col>

                  <!-- Mileage Range -->
                  <v-col cols="12" md="3">
                    <div class="filter-group">
                      <h4 class="filter-title">Mileage</h4>
                      <v-row>
                        <v-col cols="6">
                          <v-text-field
                            v-model="filters.minMileage"
                            label="Min Miles"
                            type="number"
                            outlined
                            dense
                            hide-details
                          ></v-text-field>
                        </v-col>
                        <v-col cols="6">
                          <v-text-field
                            v-model="filters.maxMileage"
                            label="Max Miles"
                            type="number"
                            outlined
                            dense
                            hide-details
                          ></v-text-field>
                        </v-col>
                      </v-row>
                    </div>
                  </v-col>
                </v-row>

                <!-- New Filter Row: Body Style, Drivetrain, Fuel Type, Transmission -->
                <v-row class="mt-3">
                  <!-- Body Style -->
                  <v-col cols="12" md="3">
                    <div class="filter-group">
                      <h4 class="filter-title">Body Style</h4>
                      <v-select
                        v-model="filters.bodyStyle"
                        :items="bodyStyleOptions"
                        label="Select Body Style"
                        outlined
                        dense
                        hide-details
                        clearable
                      ></v-select>
                    </div>
                  </v-col>

                  <!-- Drivetrain -->
                  <v-col cols="12" md="3">
                    <div class="filter-group">
                      <h4 class="filter-title">Drivetrain</h4>
                      <v-select
                        v-model="filters.drivetrain"
                        :items="drivetrainOptions"
                        label="Select Drivetrain"
                        outlined
                        dense
                        hide-details
                        clearable
                      ></v-select>
                    </div>
                  </v-col>

                  <!-- Fuel Type -->
                  <v-col cols="12" md="3">
                    <div class="filter-group">
                      <h4 class="filter-title">Fuel Type</h4>
                      <v-select
                        v-model="filters.fuelType"
                        :items="fuelTypeOptions"
                        label="Select Fuel Type"
                        outlined
                        dense
                        hide-details
                        clearable
                      ></v-select>
                    </div>
                  </v-col>

                  <!-- Transmission -->
                  <v-col cols="12" md="3">
                    <div class="filter-group">
                      <h4 class="filter-title">Transmission</h4>
                      <v-select
                        v-model="filters.transmission"
                        :items="transmissionOptions"
                        label="Select Transmission"
                        outlined
                        dense
                        hide-details
                        clearable
                      ></v-select>
                    </div>
                  </v-col>
                </v-row>

                <!-- Filter Actions -->
                <v-row class="mt-4">
                  <v-col cols="12" class="text-right">
                    <v-btn
                      text
                      color="grey darken-1"
                      @click="clearFilters"
                      class="mr-2"
                    >
                      Clear All
                    </v-btn>
                    <v-btn
                    dark
                      color="blue"
                      @click="applyFilters"
                    >
                      Apply Filters
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-container>
        </section>
      </v-expand-transition>

      <!-- Vehicles Section -->
      <section class="vehicles-section">
        <v-container>
          <!-- Vehicle Grid -->
          <div v-if="vehicles.length > 0" class="vehicles-grid">
            <TrueCarVehicleCard
              v-for="vehicle in vehicles"
              :key="vehicle.id"
              :vehicle="vehicle"
              @vehicle-clicked="viewVehicleDetail"
              @contact-dealer="openContactDealerModal"
            />
          </div>

          <!-- No vehicles message -->
          <div v-if="!loading && vehicles.length === 0" class="empty-state">
            <v-card class="empty-card mx-auto" elevation="0">
              <v-card-text class="text-center pa-12">
                <div class="empty-icon mb-6">
                  <v-icon size="120" color="grey lighten-2" class="material-icons-round">
                    directions_car
                  </v-icon>
                </div>
                <h3 class="empty-title mb-4">No Vehicles Available</h3>
                <p class="empty-subtitle mb-6">
                  We're currently updating our inventory with new arrivals.<br>
                  Please check back soon for the latest vehicles.
                </p>
                <v-btn
                  color="primary"
                  large
                  rounded
                  @click="loadVehicles"
                  class="refresh-btn"
                >
                  <v-icon left class="material-icons-round">refresh</v-icon>
                  Refresh Inventory
                </v-btn>
              </v-card-text>
            </v-card>
          </div>

          <!-- Loading more indicator -->
          <div v-if="loadingMore" class="loading-more text-center mt-8">
            <v-progress-circular
              indeterminate
              color="primary"
              size="40"
              width="3"
            ></v-progress-circular>
            <p class="loading-text mt-3">Loading more vehicles...</p>
          </div>

          <!-- Scroll trigger element -->
          <div ref="scrollTrigger" class="scroll-trigger"></div>
        </v-container>
      </section>
    </v-main>

          <!-- Fixed Bottom Footer Navigation -->
          <div class="footer-nav" :style="getStyle('footer')">
            <div class="footer-nav-container">
              <div class="footer-nav-item" @click="addCard()">
                <i aria-hidden="true" class="material-icons-round footer-icon" :style="vcard.header_color=='Light Text' ? 'color:black!important' : 'color:white!important'">contact_page</i>
                <span class="footer-label" :style="vcard.header_color=='Light Text' ? 'color:black!important' : 'color:white!important'">Card</span>
              </div>

              <div class="footer-nav-item" @click.stop="overlay = !overlay">
                <i aria-hidden="true" class="material-icons-round footer-icon" :style="vcard.header_color=='Light Text' ? 'color:black!important' : 'color:white!important'">qr_code_2</i>
                <span class="footer-label" :style="vcard.header_color=='Light Text' ? 'color:black!important' : 'color:white!important'">Scan</span>
              </div>

              <div class="footer-nav-item" @click="$router.push('/reviews')">
                <i aria-hidden="true" class="material-icons-round footer-icon" :style="vcard.header_color=='Light Text' ? 'color:black!important' : 'color:white!important'">reviews</i>
                <span class="footer-label" :style="vcard.header_color=='Light Text' ? 'color:black!important' : 'color:white!important'">Reviews</span>
              </div>

              <div class="footer-nav-item" @click="callNumber(company.phone_m)">
                <i aria-hidden="true" class="material-icons-round footer-icon" :style="vcard.header_color=='Light Text' ? 'color:black!important' : 'color:white!important'">call</i>
                <span class="footer-label" :style="vcard.header_color=='Light Text' ? 'color:black!important' : 'color:white!important'">Call</span>
              </div>
            </div>
          </div>

                  <v-dialog
                      v-model="overlay"
                      width="500"
                    >
                    <img v-if="company" @click="overlay = false" style="cursor: pointer;" :src="base_url_img+'vcard/'+company.user_id+'-QR.png'" width="50">


                  </v-dialog>

    <!-- Contact Dealer Modal -->
    <ContactDealerModal
      v-model="showContactDealerModal"
      :vehicle="selectedVehicle"
    />
  </v-app>
</template>

<script>
import PublicHeader from '@/components/PublicHeader.vue'
import ContactDealerModal from '@/components/ContactDealerModal.vue'

export default {
  name: 'Inventory',
  components: {
    PublicHeader,
    ContactDealerModal,
    TrueCarVehicleCard: () => import('./TrueCarVehicleCard.vue')
  },
  data() {
    return {
      vehicles: [],
      loading: true,
      loadingMore: false,
      currentPage: 0,
      itemsPerPage: 12,
      totalVehicles: 0,
      hasMoreVehicles: true,
      observer: null,
      searchQuery: '',
      showFilters: false,
      currentSort: { value: 'price_asc', label: 'Price: Low to High' },
      sortOptions: [
        { value: 'price_asc', label: 'Price: Low to High' },
        { value: 'price_desc', label: 'Price: High to Low' },
        { value: 'year_desc', label: 'Year: Newest First' },
        { value: 'year_asc', label: 'Year: Oldest First' },
        { value: 'mileage_asc', label: 'Mileage: Low to High' },
        { value: 'mileage_desc', label: 'Mileage: High to Low' }
      ],
      filters: {
        minPrice: null,
        maxPrice: null,
        make: null,
        minYear: null,
        maxYear: null,
        minMileage: null,
        maxMileage: null,
        bodyStyle: null,
        drivetrain: null,
        fuelType: null,
        transmission: null
      },
      makeOptions: [],
      // Filter options from VehicleDetail.vue
      bodyStyleOptions: ['Coupe','Convertible','Sedan','Hybrid/Electric','Crossover/SUV','Truck','4WD/AWD','Van','Wagon','Hatchback'],
      drivetrainOptions: ['4x4', 'AWD', 'FWD', 'RWD'],
      fuelTypeOptions: ["Gasoline", "Diesel", "Electric", "Hybrid", "Plug-in Hybrid", "CNG", "LPG", "Hydrogen", "Ethanol", "Biodiesel", "Synthetic Fuels", "Solar"],
      transmissionOptions: ['Automatic', 'Manual', 'CVT'],
      searchTimeout: null,
      filterTimeout: null,
      showContactDealerModal: false,
      selectedVehicle: null,
      // Footer navigation data
      overlay: false,
      vcard: null,
      company: null,
      base_url_img: process.env.VUE_APP_WASBI_URL
    }
  },
  async created() {
    await this.loadVehicles()
  },
  computed: {
    hasActiveFilters() {
      return Object.values(this.filters).some(value => value !== null && value !== '')
    },

    activeFiltersCount() {
      return Object.values(this.filters).filter(value => value !== null && value !== '').length
    }
  },
  mounted() {
    this.loadVehicles()
    this.loadMakeOptions()
    this.loadCardData()
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect()
    }
  },
  methods: {
    setupInfiniteScroll() {
      this.observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && this.hasMoreVehicles && !this.loadingMore && !this.loading) {
          this.loadMoreVehicles()
        }
      }, {
        threshold: 0.1
      })

      if (this.$refs.scrollTrigger) {
        this.observer.observe(this.$refs.scrollTrigger)
      }
    },

    async loadVehicles(resetPage = true) {
      try {
        if (resetPage) {
          this.loading = true
          this.currentPage = 0
          this.vehicles = []
        } else {
          this.loadingMore = true
        }

        const params = {
          limit: this.itemsPerPage,
          offset: this.currentPage * this.itemsPerPage
        }

        // Add search parameter
        if (this.searchQuery) {
          params.search = this.searchQuery
        }

        // Add filter parameters
        if (this.filters.minPrice) {
          params.min_price = this.filters.minPrice
        }
        if (this.filters.maxPrice) {
          params.max_price = this.filters.maxPrice
        }
        if (this.filters.make) {
          params.make = this.filters.make
        }
        if (this.filters.minYear) {
          params.min_year = this.filters.minYear
        }
        if (this.filters.maxYear) {
          params.max_year = this.filters.maxYear
        }
        if (this.filters.minMileage) {
          params.min_mileage = this.filters.minMileage
        }
        if (this.filters.maxMileage) {
          params.max_mileage = this.filters.maxMileage
        }
        if (this.filters.bodyStyle) {
          params.body_style = this.filters.bodyStyle
        }
        if (this.filters.drivetrain) {
          params.drivetrain = this.filters.drivetrain
        }
        if (this.filters.fuelType) {
          params.fuel_type = this.filters.fuelType
        }
        if (this.filters.transmission) {
          params.transmission = this.filters.transmission
        }

        // Add sort parameter
        params.sort = this.currentSort.value

        const response = await axios.get('/api/vehicles/all', { params })

        if (response.data.flag === 1) {
          const newVehicles = response.data.vehicles || []

          if (resetPage) {
            this.vehicles = newVehicles
          } else {
            this.vehicles = [...this.vehicles, ...newVehicles]
          }

          this.totalVehicles = response.data.total || 0
          this.hasMoreVehicles = response.data.has_more || false

          if (resetPage) {
            this.currentPage = 1
          } else {
            this.currentPage++
          }
        } else {
          console.error('API returned error:', response.data)
          this.$toast?.error('Failed to load vehicles') || console.error('Failed to load vehicles')
        }
      } catch (error) {
        console.error('Error loading vehicles:', error)
        if (this.$toast) {
          this.$toast.error('Error loading vehicles')
        } else {
          alert('Error loading vehicles')
        }
      } finally {
        this.loading = false
        this.loadingMore = false

        if (resetPage) {
          // Setup infinite scroll after initial load
          this.$nextTick(() => {
            this.setupInfiniteScroll()
          })
        }
      }
    },

    async loadMoreVehicles() {
      if (this.hasMoreVehicles && !this.loadingMore && !this.loading) {
        await this.loadVehicles(false)
      }
    },

    viewVehicleDetail(vehicleId) {
      // Navigate to existing vehicle detail page
      this.$router.push(`/view-inventory-details/${vehicleId}`)
    },

    onSearchInput() {
      // Debounce search input
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.performSearch()
      }, 500)
    },

    async performSearch() {
      await this.loadVehicles(true)
    },

    changeSortOrder(sort) {
      this.currentSort = sort
      this.loadVehicles(true)
    },

    async loadMakeOptions() {
      try {
        // Load unique makes from API
        const response = await axios.get('/api/get-vehicle-makes')
        if (response.data.flag === 1) {
          this.makeOptions = response.data.makes.map(make => make.make) || []
        }
      } catch (error) {
        console.error('Error loading make options:', error)
        // Fallback: extract makes from current vehicles
        this.extractMakeOptionsFromVehicles()
      }
    },

    extractMakeOptionsFromVehicles() {
      const makes = [...new Set(this.vehicles
        .map(vehicle => vehicle.make)
        .filter(make => make)
        .sort()
      )]
      this.makeOptions = makes
    },



    applyFilters() {
      // Auto-collapse the filter panel
      this.showFilters = false

      // Debounce filter application
      clearTimeout(this.filterTimeout)
      this.filterTimeout = setTimeout(() => {
        this.loadVehicles(true)
      }, 300)
    },

    clearFilters() {
      // Auto-collapse the filter panel
      this.showFilters = false

      this.filters = {
        minPrice: null,
        maxPrice: null,
        make: null,
        minYear: null,
        maxYear: null,
        minMileage: null,
        maxMileage: null,
        bodyStyle: null,
        drivetrain: null,
        fuelType: null,
        transmission: null
      }
      this.loadVehicles(true)
    },

    openContactDealerModal(vehicle) {
      this.selectedVehicle = vehicle
      this.showContactDealerModal = true
    },

    // Footer navigation methods
    async loadCardData() {
      // Load card/vcard data for footer styling
      const domain = localStorage.getItem('card_domain')
      if (domain) {
        try {
          const response = await this.$axios.post('/api/get-card', { id: domain })
          if (response.data.flag === 1) {
            this.company = response.data.user
            this.vcard = response.data.vcard ? response.data.vcard[0] : null
          }
        } catch (error) {
          console.error('Error loading card data:', error)
        }
      }
    },

    addCard() {
      // Navigate to the card page which handles add to contacts
      const domain = localStorage.getItem('card_domain')
      if (domain) {
        this.$router.push('/' + domain)
      } else {
        this.$router.push('/')
      }
    },

    callNumber(phone) {
      if (phone) {
        window.location.href = 'tel:' + phone
      }
    },

    getStyle(flag) {
      if (!this.vcard || !this.vcard.color) {
        return 'background-color:#000000';
      }
      let color = this.vcard.color;
      let color_code = '';
      const colorArray = [
        { name: 'Orange', code: '#FD7E14' },
        { name: 'Green', code: '#28A745' },
        { name: 'Purple', code: '#6F42C1' },
        { name: 'Red', code: '#DC3545' },
        { name: 'Pink', code: '#E83E8C' },
        { name: 'Blue', code: '#007BFF' },
        { name: 'Dark Blue', code: '#0033cc' },
        { name: 'Black', code: '#000000' },
        { name: 'Silver', code: '#C0C0C0' },
        { name: 'Hot Pink', code: '#FF69B4' },
        { name: 'Flat Black', code: '#2D3436' }
      ];
      colorArray.forEach(element => {
        if (element.name == color) {
          color_code = element.code;
        }
      });
      if (flag == 'footer') {
        return 'background-color:' + color_code;
      }
      return '';
    }
  }
}
</script>

<style scoped>
/* Search Header */
.search-header {
  background: white;
  border-bottom: 1px solid #e5e7eb;
  padding: 2rem 0;
}

.search-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.search-title-section {
  text-align: left;
}

.search-title {
  font-size: 2.25rem !important;
  font-weight: 800 !important;
  color: #111827 !important;
  margin-bottom: 0.5rem;
  letter-spacing: -0.025em;
  line-height: 1.2;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Roboto', 'Helvetica Neue', Arial, sans-serif;
}

.search-subtitle {
  font-size: 1.125rem;
  color: #4b5563;
  margin: 0;
  font-weight: 500;
  line-height: 1.4;
}

/* Search and Filter Bar */
.search-filter-bar {
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
}

.search-input-section {
  flex: 1;
  min-width: 300px;
}

.search-input {
  max-width: 500px;
}

.search-input >>> .v-input__control {
  min-height: 44px !important;
}

.search-input >>> .v-text-field__details {
  display: none;
}

.search-input >>> .v-input__prepend-inner {
  margin-top: 12px;
  margin-right: 8px;
}

.filter-buttons {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.filter-btn {
  text-transform: none !important;
  font-weight: 500 !important;
  font-size: 0.875rem !important;
  color: #374151 !important;
  border-color: #d1d5db !important;
  height: 44px !important;
  border-radius: 6px !important;
}

.filter-btn:hover {
  background-color: #f9fafb !important;
  border-color: #9ca3af !important;
}

/* Filter Panel */
.filter-panel {
  background: #f8f9fa;
  border-bottom: 1px solid #e5e7eb;
  padding: 1.5rem 0;
}

.filter-card {
  background: white;
  border-radius: 8px !important;
  border: 1px solid #e5e7eb;
}

.filter-content {
  padding: 1.5rem !important;
}

.filter-group {
  margin-bottom: 1rem;
}

.filter-title {
  font-size: 0.875rem !important;
  font-weight: 600 !important;
  color: #374151 !important;
  margin-bottom: 0.75rem !important;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.filter-group >>> .v-text-field__details,
.filter-group >>> .v-select__details {
  display: none;
}

.filter-group >>> .v-input__control {
  min-height: 40px !important;
}

.filter-group >>> .v-text-field--outlined fieldset,
.filter-group >>> .v-select--outlined fieldset {
  border-color: #d1d5db !important;
}

.filter-group >>> .v-text-field--outlined:hover fieldset,
.filter-group >>> .v-select--outlined:hover fieldset {
  border-color: #9ca3af !important;
}

/* Vehicles Section */
.vehicles-section {
  background: #f9fafb;
  min-height: 60vh;
  padding: 2rem 0;
}

.vehicles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;
}

/* Empty State */
.empty-state {
  padding: 4rem 0;
}

.empty-card {
  background: #f8fafc !important;
  border: 2px dashed #cbd5e1;
  border-radius: 20px !important;
  max-width: 600px;
}

.empty-icon {
  opacity: 0.6;
}

.empty-title {
  font-size: 1.75rem !important;
  font-weight: 600 !important;
  color: #374151 !important;
}

.empty-subtitle {
  font-size: 1rem;
  color: #6b7280;
  line-height: 1.6;
}

.refresh-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  padding: 12px 32px !important;
  box-shadow: 0 4px 14px rgba(59, 130, 246, 0.3) !important;
}

/* Loading More */
.loading-more {
  padding: 2rem 0;
}

.loading-text {
  color: #64748b;
  font-weight: 500;
  margin: 0;
}

.scroll-trigger {
  height: 1px;
  margin-top: 2rem;
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.hero-content {
  animation: fadeInUp 1s ease-out;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .vehicles-grid {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1.5rem;
  }
}

@media (max-width: 960px) {
  .search-header {
    padding: 1.5rem 0;
  }

  .search-content {
    gap: 1.5rem;
  }

  .search-title {
    font-size: 1.75rem !important;
  }

  .search-filter-bar {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .search-input-section {
    min-width: auto;
  }

  .filter-buttons {
    justify-content: space-between;
  }

  .filter-panel {
    padding: 1rem 0;
  }

  .filter-content {
    padding: 1rem !important;
  }

  .vehicles-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1rem;
  }

  .vehicles-section {
    padding: 1.5rem 0;
  }
}

@media (max-width: 600px) {
  .search-header {
    padding: 1rem 0;
  }

  .search-title {
    font-size: 1.5rem !important;
  }

  .search-subtitle {
    font-size: 0.875rem;
  }

  .filter-buttons {
    flex-direction: column;
    gap: 0.75rem;
  }

  .filter-btn {
    width: 100%;
    justify-content: center;
  }

  .filter-panel {
    padding: 0.75rem 0;
  }

  .filter-content {
    padding: 0.75rem !important;
  }

  .filter-title {
    font-size: 0.8rem !important;
    margin-bottom: 0.5rem !important;
  }

  .vehicles-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .vehicles-section {
    padding: 1rem 0;
    padding-bottom: 100px;
  }
}

/* Fixed Bottom Footer Navigation */
.footer-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 75px;
  z-index: 1000;
  box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.15),
              0 -2px 8px rgba(0, 0, 0, 0.1),
              inset 0 1px 0 rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(30px) saturate(180%);
  background: linear-gradient(180deg,
    rgba(30, 41, 59, 0.95) 0%,
    rgba(30, 41, 59, 0.98) 100%);
  border-top: 1px solid rgba(255, 255, 255, 0.15);
}

.footer-nav-container {
  display: flex;
  justify-content: space-around;
  align-items: center;
  height: 100%;
  max-width: 600px;
  margin: 0 auto;
  padding: 0 12px;
}

.footer-nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 12px 16px;
  border-radius: 20px;
  min-width: 80px;
  position: relative;
}

.footer-nav-item:hover {
  transform: translateY(-3px);
  background: rgba(255, 255, 255, 0.12);
}

.footer-nav-item:active {
  transform: translateY(-1px);
}

.footer-icon {
  font-size: 28px;
  margin-bottom: 5px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 1;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.12));
}

.footer-nav-item:hover .footer-icon {
  transform: scale(1.1);
}

.footer-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  position: relative;
  z-index: 1;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

/* Add padding to content to account for fixed footer */
.vehicles-section {
  padding-bottom: 100px !important;
}

@media (max-width: 768px) {
  .footer-nav {
    height: 65px;
  }

  .footer-icon {
    font-size: 24px;
  }

  .footer-label {
    font-size: 9px;
  }

  .footer-nav-item {
    padding: 10px 12px;
    min-width: 70px;
  }
}

@media (max-width: 480px) {
  .footer-nav {
    height: 60px;
  }

  .footer-icon {
    font-size: 22px;
    margin-bottom: 3px;
  }

  .footer-label {
    font-size: 8px;
  }

  .footer-nav-item {
    padding: 8px 10px;
    min-width: 60px;
  }
}
</style>
